import { ReactNode } from "react";
import { v4 as uuidv4 } from 'uuid';
import { TreeViewDataItem } from "@patternfly/react-core";
import { isString } from "lodash-es";

import { ModelVisibility, Model, CustomizationBase, OutputMetadataItem, AeTitleRule, DEFAULT_SUBSCRIPTION, DicomRule, DicomAttributeRule, ModelType, CustomizationTreeViewDataItem, DEFAULT_MODEL_CUSTOMIZATION_NAME, Modality, getModelTagsInOrder, ValidationStatus } from "./customization-types";
import { getIconByType, TreeViewIconType } from "../../components/icon-helper";
import { enumFromStringValue, isSomeEnum } from "../../util/enum";
import { BODY_PART_TAB_BRAIN, BODY_PART_TAB_HEAD, BODY_PART_TAB_HEADNECK, BODY_PART_TAB_TORSO, BODY_PART_TAB_ABDOMEN_CHEST, BODY_PART_TAB_PELVIS, BODY_PART_TAB_COMBINATION, BODY_PART_TAB_WHOLE_BODY } from "../../components/body-part-icon";
import { naturalSortInPlace } from "../../util/sort";

/** Canonical display order and translation keys for known body parts. */
export const BODY_PART_DISPLAY_ORDER = [
    { id: BODY_PART_TAB_BRAIN, translationKey: 'bodyPart.category.brain' },
    { id: BODY_PART_TAB_HEAD, translationKey: 'bodyPart.category.head' },
    { id: BODY_PART_TAB_HEADNECK, translationKey: 'bodyPart.category.headNeck' },
    { id: BODY_PART_TAB_TORSO, translationKey: 'bodyPart.category.torso' },
    { id: BODY_PART_TAB_ABDOMEN_CHEST, translationKey: 'bodyPart.category.abdomenChest' },
    { id: BODY_PART_TAB_PELVIS, translationKey: 'bodyPart.category.pelvis' },
    { id: BODY_PART_TAB_COMBINATION, translationKey: 'bodyPart.category.combination' },
    { id: BODY_PART_TAB_WHOLE_BODY, translationKey: 'bodyPart.category.wholeBody' },
] as const;

/** Sorts body part tree view items by display order. Unknown body parts are appended at the end in natural sort order. */
export const sortByBodyPartDisplayOrder = (bodyPartNodes: CustomizationTreeViewDataItem[]): CustomizationTreeViewDataItem[] => {
    const orderIndex: Map<string, number> = new Map(BODY_PART_DISPLAY_ORDER.map((bp, i) => [bp.id, i]));
    const known: CustomizationTreeViewDataItem[] = [];
    const unknown: CustomizationTreeViewDataItem[] = [];

    for (const node of bodyPartNodes) {
        if (orderIndex.has(node.id)) {
            known.push(node);
        } else {
            unknown.push(node);
        }
    }

    known.sort((a, b) => orderIndex.get(a.id)! - orderIndex.get(b.id)!);
    naturalSortInPlace(unknown, 'label');

    return [...known, ...unknown];
};

/** JSON attribute that is inserted into outgoing customization JSON to make help identifying error message target components easier. */
export const UI_ID_ATTRIBUTE = 'ui_id';

const MODEL_VISIBILITY_JSON_VALUE_DEFAULT = 'default';
const MODEL_VISIBILITY_JSON_VALUE_ALWAYS_SHOW = 'always_show';
const MODEL_VISIBILITY_JSON_VALUE_ALWAYS_HIDE = 'always_hide';

export const createNewModel = (
  modelName: string,
  modelType: ModelType,
  label: string,
  bodyPart: string,
  description: string,
  visibility: ModelVisibility,
  tags: string[],
  inputModalities: Modality[],
  outputModalities: Modality[],
  customizationIds: string[],
  isDeprecated: boolean = false,
  isLatest: boolean = false,
  version?: string,
  id?: string,
  lastModifiedTimestamp?: number
): Model => {
  return {
    id: id || generateNewId(),
    modelType: modelType,
    modelName: modelName,
    label: label,
    bodyPart: bodyPart,
    description: description,
    visibility: visibility,
    // ensure that tags are in the correct order when we render these in UI
    tags: getModelTagsInOrder(tags),
    inputModalities: inputModalities,
    outputModalities: outputModalities,
    customizations: customizationIds,
    // TODO: add model_type support
    isModified: false,
    isAvailable: true,
    isDeprecated: isDeprecated,
    isLatest: isLatest,
    version: version || "",
    lastModifiedTimestamp: lastModifiedTimestamp,
  };
};

export const createNewModelCustomizationBase = (customizationName: string, description: string, outputIds: string[], aeTitleRuleIds: string[], dicomRuleIds: string[], modelId: string, id?: string, isModified?: boolean): CustomizationBase => ({
    id: id || generateNewId(),
    customizationName: customizationName,
    description: description,
    outputs: outputIds,
    aeTitleRules: aeTitleRuleIds,
    dicomRules: dicomRuleIds,
    modelId: modelId,
    isModified: isModified || false,
});

export const createNewMetadataItem = (attribute: string, value: string, modelCustomizationOutputId?: string, id?: string): OutputMetadataItem => ({
    id: id || generateNewId(),
    attribute: attribute,
    value: value,
    isModified: false,
    modelCustomizationOutputId: modelCustomizationOutputId,
    scrollToView: false,
});

export const createNewAeTitleRule = (action?: string, aggregation?: string | null, subscription?: string,
    isEditable?: boolean, id?: string, modelCustomizationBaseId?: string, isNew?: boolean, isModified?: boolean,): AeTitleRule => ({
        id: id || generateNewId(),
        action: action || '',
        modelCustomizationBaseId: modelCustomizationBaseId || undefined,
        aggregation: aggregation || null,
        subscription: subscription || DEFAULT_SUBSCRIPTION,
        isEditable: isEditable || true,
        isModified: isModified || false,
        isNew: isNew || false,
    });

export const createNewDicomRule = (dicomAttributeIds: string[], id?: string, modelCustomizationBaseId?: string,
    aggregation?: string | null, subscription?: string, isNew?: boolean, isModified?: boolean): DicomRule => ({
        id: id || generateNewId(),
        modelCustomizationBaseId: modelCustomizationBaseId || undefined,
        dicomAttributes: dicomAttributeIds,
        aggregation: aggregation || null,
        subscription: subscription || DEFAULT_SUBSCRIPTION,
        isEditable: true,
        isModified: isModified || false,
        isNew: isNew || false,
    });

export const createNewDicomAttributeRule = (attribute: string, value: string, parentDicomRuleId: string, isNew?: boolean, id?: string): DicomAttributeRule => ({
    id: id || generateNewId(),
    attribute: attribute,
    value: value,
    parentDicomRuleId: parentDicomRuleId,
    isNew: isNew || false,
    isModified: false,
});

/** Parses model visibility enum for view models from a JSON value. */
export const parseModelVisibility = (modelVisibilityJson: string | undefined): ModelVisibility => {
    if (modelVisibilityJson) {
        if (modelVisibilityJson === MODEL_VISIBILITY_JSON_VALUE_ALWAYS_SHOW) { return ModelVisibility.AlwaysShown; }
        else if (modelVisibilityJson === MODEL_VISIBILITY_JSON_VALUE_ALWAYS_HIDE) { return ModelVisibility.AlwaysHidden; }
        else if (modelVisibilityJson === MODEL_VISIBILITY_JSON_VALUE_DEFAULT) { return ModelVisibility.Default; }
        else { throw new Error(`Unsupported model visibility value: ${modelVisibilityJson}`); }
    }

    return ModelVisibility.Default;
}

/** Converts view model visibility enum into matching JSON value for backend. */
export const getModelVisibilityForJson = (modelVisibility: ModelVisibility): string => {
    switch (modelVisibility) {
        case ModelVisibility.Default:
            return MODEL_VISIBILITY_JSON_VALUE_DEFAULT;
        case ModelVisibility.AlwaysShown:
            return MODEL_VISIBILITY_JSON_VALUE_ALWAYS_SHOW;
        case ModelVisibility.AlwaysHidden:
            return MODEL_VISIBILITY_JSON_VALUE_ALWAYS_HIDE;
        default:
            throw new Error('Unsupported model visibility value');
    }
}

/** Parses supported modalities for view models from a JSON array. */
export const parseModelModalities = (modalitiesJson: string[] | undefined): Modality[] => {
    if (modalitiesJson) {
        const modalities: Modality[] = [];
        for (const modalityJson of modalitiesJson) {
            if (isSomeEnum(Modality)(modalityJson)) {
                modalities.push(enumFromStringValue(Modality, modalityJson) as Modality);
            } else {
                throw new Error(`Unsupported modality: ${modalityJson}`);
            }
        }
        return modalitiesJson.map((modality) => Modality[modality as keyof typeof Modality]);
    }

    return [];
}

/** Converts supported modalities enum into matching JSON value for backend. */
export const getModelModalitiesForJson = (modalities: Modality[]): string[] => {
    return modalities.map((modality) => Modality[modality as keyof typeof Modality]);
}

export const generateNewId = (): string => {
    return uuidv4();
}

/** Returns a human-readable label that includes a model's name (label) and the name of its customization (if other than default) */
export const generateCustomizationLabel = (modelLabel: string, customizationName: string): string => {
    return customizationName !== DEFAULT_MODEL_CUSTOMIZATION_NAME ? `${modelLabel} (${customizationName})` : modelLabel;
}

export const duplicateModelCustomizationMetadataItem = (metadata: OutputMetadataItem, outputId?: string): OutputMetadataItem => {
    return {
        id: generateNewId(),
        attribute: metadata.attribute,
        value: metadata.value,
        isModified: true,
        modelCustomizationOutputId: outputId,
        scrollToView: false,
    };
}

/** Enables (or disables) customization tree view behaviour where selecting a branch item (such as a model name, e.g. 'Breast + Abdomen CT' or
 * a customization name, e.g. 'default') select the leaf node (e.g. 'mv-rt.dcm'). This will also have an effect on certain corner cases of what
 * the customization sidebar drawer remembers from the tree view selection when the drawer is collapsed and re-expanded.
 */
export const ENABLE_TREE_VIEW_BRANCHES_SELECT_LEAF_ITEM_BEHAVIOUR = false;

/** Helper function for convertTreeViewDataIntoItemHierarchy. Returns true if the item specified here
 * should be rendered and false if it (and its children) should be hidden.
 */
export const showCustomizationTreeViewDataItem = (
    item: CustomizationTreeViewDataItem,
    includeNonLicensedModels: boolean,
    includeDeprecatedModels: boolean,
    includeUserHiddenModels: boolean,
    ignoreValidity: boolean = false): boolean => {

    // always show items that are set as 'always shown'
    if (item.modelVisibility === ModelVisibility.AlwaysShown) { return true; }

    // always show items with errors so user can identify and fix them
    // 'ignoreValidity' is used when double-checking if the item would've been hidden
    // if it had been valid
    if (!ignoreValidity && item.validationStatus !== ValidationStatus.Valid) { return true; }

    // only show items set as 'always hide' if user has opted to show hidden items
    if (item.modelVisibility === ModelVisibility.AlwaysHidden) { return includeUserHiddenModels; }

    // don't show items that aren't available unless specific filtering setting is on
    if (!item.isAvailable && !includeNonLicensedModels) { return false; }

    // don't show deprecated items unless specific filtering setting is on
    if (item.isDeprecated && !includeDeprecatedModels) { return false; }

    return true;
}

/** Helper function for converting hierarchical customization data (as generated in a separate selectors in
 * model type slices, such as selectCustomizationTreeViewItems in contouringSelectors.ts) into an object 
 * hierarchy that can be read by a Patternfly TreeView component.
 */
export const convertTreeViewDataIntoItemHierarchy = (
    customizationHierarchy: CustomizationTreeViewDataItem[],
    getTreeViewItemName: (item: CustomizationTreeViewDataItem, noLicenseWarning: string, tags?: string[], nameOverride?: string) => ReactNode,
    noLicenseWarning: string,
    headerItems: TreeViewDataItem[],
    includeNonLicensedModels: boolean,
    includeDeprecatedModels: boolean,
    includeUserHiddenModels: boolean
): TreeViewDataItem[] => {

    // change headerItems name when a file is modified using getTreeViewItemName function
    for (const headerItem of headerItems) {
        const customizationItem = customizationHierarchy.find(c => c.id === headerItem.id);
        if (customizationItem && (customizationItem.isModified || customizationItem.validationStatus !== ValidationStatus.Valid)) {
            const nameOverride = isString(headerItem.name) ? headerItem.name : undefined;
            headerItem.name = getTreeViewItemName(customizationItem, noLicenseWarning, undefined, nameOverride);
        }
    }

    // generate treeview data from memoized customization item hierarchy
    const data: TreeViewDataItem[] = [...headerItems];

    for (const bodyPartItem of customizationHierarchy) {
        if (!showCustomizationTreeViewDataItem(bodyPartItem, includeNonLicensedModels, includeDeprecatedModels, includeUserHiddenModels)) {
            continue;
        }

        let bodyPartItemData = data.find(p => p.id === bodyPartItem.id);
        if (!bodyPartItemData) {
            bodyPartItemData = {
                name: getTreeViewItemName(bodyPartItem, noLicenseWarning),
                id: bodyPartItem.id,
                children: [],

            };
            data.push(bodyPartItemData);
        } else if (!bodyPartItemData.children) {
            bodyPartItemData.children = [];
        }

        for (const modelItem of bodyPartItem.children) {
            if (!showCustomizationTreeViewDataItem(modelItem, includeNonLicensedModels, includeDeprecatedModels, includeUserHiddenModels)) {
                continue;
            }

            const modelItemData: TreeViewDataItem = {
                name: getTreeViewItemName(modelItem, noLicenseWarning, modelItem.tags),
                id: modelItem.id,
                children: modelItem.children.length > 0 ? [] : undefined,
                icon: getIconByType(TreeViewIconType.Folder),
                expandedIcon: getIconByType(TreeViewIconType.Folder),
            };

            if (ENABLE_TREE_VIEW_BRANCHES_SELECT_LEAF_ITEM_BEHAVIOUR && modelItem.children.length > 0) {
                // put either the 'default' or first customization base as the open id for the model
                const defaultCustomization = modelItem.children.find(c => c.label === DEFAULT_MODEL_CUSTOMIZATION_NAME);
                modelItemData.id = defaultCustomization ? defaultCustomization.id : modelItem.children[0].id;
            }

            for (const baseItem of modelItem.children) {
                if (!showCustomizationTreeViewDataItem(baseItem, includeNonLicensedModels, includeDeprecatedModels, includeUserHiddenModels)) {
                    continue;
                }

                let baseItemData: TreeViewDataItem = {
                    name: getTreeViewItemName(baseItem, noLicenseWarning),
                    id: baseItem.id,
                    children: baseItem.children.length > 0 ? [] : undefined,
                    // always open file-level items
                    defaultExpanded: true,
                    icon: getIconByType(TreeViewIconType.Gear)
                };

                if (ENABLE_TREE_VIEW_BRANCHES_SELECT_LEAF_ITEM_BEHAVIOUR && baseItem.children.length > 0) {
                    // put first file in the list as the open id for the customization base
                    baseItemData.id = baseItem.children[0].id;

                    // also finish up setting the correct id for the model
                    if (modelItemData.id === baseItem.id) {
                        modelItemData.id = baseItem.children[0].id;
                    }
                }

                for (const outputItem of baseItem.children) {
                    if (!showCustomizationTreeViewDataItem(outputItem, includeNonLicensedModels, includeDeprecatedModels, includeUserHiddenModels)) {
                        continue;
                    }

                    const outputItemData: TreeViewDataItem = {
                        name: baseItem.children.length > 1 ? getTreeViewItemName(outputItem, noLicenseWarning) : baseItemData.name,
                        id: outputItem.id,
                        icon: baseItem.children.length > 1 ? getIconByType(TreeViewIconType.File) : getIconByType(TreeViewIconType.Gear),
                    };

                    // If the children is just 1 skip rendering the output item and instead assign the output item to the base Item
                    if (baseItem.children.length > 1) {
                        baseItemData.children!.push(outputItemData);
                    }
                    else {
                        baseItemData = outputItemData;
                    }
                }

                modelItemData.children!.push(baseItemData);
            }

            bodyPartItemData.children!.push(modelItemData);
        }
    }

    // remove empty header nodes
    const filteredData = data.filter(n => n.children && n.children.length > 0);

    return filteredData;
}

// *** pass-through field helpers ***

/** Collect known pass-through fields from a JSON entry, storing each as a JSON-stringified string. */
export const collectPassthroughFields = (entry: unknown, allowedFields: string[]): Record<string, string> => {
    if (!entry || typeof entry !== 'object') { return {}; }
    const record = entry as Record<string, unknown>;
    const result: Record<string, string> = {};
    for (const key of allowedFields) {
        if (record[key] !== undefined) {
            result[key] = JSON.stringify(record[key]);
        }
    }
    return result;
};

/** Spread pass-through fields back into a JSON object by parsing the stored JSON strings. */
export const spreadPassthroughFields = (fields: Record<string, string>): Record<string, unknown> =>
    Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, JSON.parse(v)]));
